// src/store/index.js
import { createStore } from "vuex";
import router from "@/router";
import credits from "./modules/credits";
import referral from "./modules/referral";
import learning from "./modules/edupage/learning";
import ui from "./modules/ui";

function normalizeOwnedNoteId(rawId) {
  if (rawId === null || rawId === undefined) return null;
  const asString = String(rawId).trim();
  if (!asString) return null;

  const asNumber = Number(asString);
  if (!Number.isNaN(asNumber)) {
    return asNumber;
  }

  return asString;
}

function normalizeOwnedNotes(notesData) {
  if (!Array.isArray(notesData)) return [];

  const seen = new Set();
  const normalized = [];

  for (const note of notesData) {
    const rawId = note && typeof note === "object" ? note.id : note;
    const normalizedId = normalizeOwnedNoteId(rawId);
    if (normalizedId === null) continue;

    const dedupeKey = String(normalizedId);
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);

    if (note && typeof note === "object") {
      normalized.push({ ...note, id: normalizedId });
    } else {
      normalized.push({ id: normalizedId });
    }
  }

  return normalized;
}

let inviteCodeInflight = null;

export default createStore({
  modules: {
    credits,
    referral,
    edupage: {
      namespaced: true,
      modules: {
        learning,
      },
    },
    ui,
  },
  state: {
    user: null,
    userRoles: { roles: [] },
    prihlasenyStav: 0,
    userCart: {},
    api: (
      process.env.VUE_APP_API_BASE_URL || "https://heligonkovaakademia.sk/api"
    ).replace(/\/+$/, ""),
    SnackBarText: "",
    info: { gratulation: { show: false, message: "" } },
    isLoaded: false,
    authReady: false,
    authChecked: false,
    authLoggedIn: false,
    authIsAdmin: false,
    ciselnyZapisObrazky: [],
    overlayVisible: false,
    globalVariables: {},
    inviteCode: "",
    inviteCodeFetchedAt: 0,
  },
  getters: {},
  mutations: {
    SET_USER(state, userData) {
      const wasAdmin =
        state.user?.isAdmin === true || state.authIsAdmin === true;
      state.user = userData;
      if (!state.user) state.user = {};
      if (wasAdmin) state.user.isAdmin = true;

      if (state.user.ads == "true") state.user.ads = true;
      else state.user.ads = false;

      window.haCanPrint = !!(
        state.user?.edupage_in_calendar ||
        (state.user?.isAdmin &&
          state.userRoles?.roles?.includes("admin_master"))
      );
    },
    SET_USER_DATA(state, payload) {
      state.userRoles.roles = payload;
      window.haCanPrint = !!(
        state.user?.edupage_in_calendar ||
        (state.user?.isAdmin &&
          state.userRoles?.roles?.includes("admin_master"))
      );
    },
    ADD_USER_INFO_VLASTNENE_ZAPISY(state, notesData) {
      if (!Array.isArray(state.user?.ownedNotes)) {
        if (!state.user) state.user = {};
        state.user.ownedNotes = [];
      }
      const current = normalizeOwnedNotes(state.user.ownedNotes);
      const incoming = normalizeOwnedNotes(notesData);
      state.user.ownedNotes = normalizeOwnedNotes([...current, ...incoming]);
    },
    SET_USER_INFO_VLASTNENE_ZAPISY(state, notesData) {
      if (!state.user) state.user = {};
      state.user.ownedNotes = normalizeOwnedNotes(notesData);
    },
    ADD_USER_INFO_KOSIK(state, cartData) {
      state.userCart = {};
      state.userCart = cartData;
    },
    DELETE_USER_INFO_KOSIK(state) {
      state.user = [];
    },
    ADD_USER_ADMIN(state) {
      if (!state.user) state.user = {};
      state.user.isAdmin = true;
      state.authIsAdmin = true;
      window.haCanPrint = !!(
        state.user?.edupage_in_calendar ||
        (state.user?.isAdmin &&
          state.userRoles?.roles?.includes("admin_master"))
      );
    },
    ADD_USER_ACTIVE_SUBSCRIPTION(state) {
      if (!state.user) state.user = {};
      state.user.isSubscribed = true;
    },
    REMOVE_USER_ACTIVE_SUBSCRIPTION(state) {
      if (!state.user) state.user = {};
      state.user.isSubscribed = false;
      console.log(JSON.stringify(state.user));
    },
    SET_GRATULATION(state, value) {
      const current =
        typeof state.info.gratulation === "object"
          ? state.info.gratulation
          : { show: !!state.info.gratulation, message: "" };

      if (typeof value === "string") {
        state.info.gratulation = { show: true, message: value };
      } else if (typeof value === "boolean") {
        state.info.gratulation = { ...current, show: value };
      } else if (value && typeof value === "object") {
        state.info.gratulation = {
          show: value.show ?? current.show,
          message: value.message ?? current.message,
        };
      } else {
        state.info.gratulation = current;
      }
    },
    SET_INVITE_CODE(state, code) {
      state.inviteCode = code;
      state.inviteCodeFetchedAt = Date.now();
    },
    setInviteCode(state, code) {
      state.inviteCode = code;
      state.inviteCodeFetchedAt = Date.now();
    },
    SET_AUTH_READY(state, value) {
      state.authReady = !!value;
    },
  },
  actions: {
    async ensureInviteCode({ state, commit }, payload = {}) {
      const codeFromQuery =
        payload && typeof payload.codeFromQuery !== "undefined"
          ? String(payload.codeFromQuery || "").trim()
          : "";
      if (codeFromQuery) {
        commit("SET_INVITE_CODE", codeFromQuery);
        return codeFromQuery;
      }

      if (state.inviteCode) return state.inviteCode;
      if (inviteCodeInflight) return inviteCodeInflight;

      inviteCodeInflight = (async () => {
        try {
          const res = await fetch(`${state.api}/user/info/getInviteCode.php`, {
            credentials: "include",
          });
          const result = await res.json().catch(() => null);
          const code =
            result?.status === "Request succesfull" && result?.data
              ? String(result.data)
              : "neznamy";
          commit("SET_INVITE_CODE", code);
          return code;
        } catch {
          commit("SET_INVITE_CODE", "neznamy");
          return "neznamy";
        } finally {
          inviteCodeInflight = null;
        }
      })();

      return inviteCodeInflight;
    },

    prihlasenie({ state }, { email, heslo }) {
      this.dispatch("odhlasenie");
      setTimeout(() => {
        const axios = require("axios");
        const FormData = require("form-data");
        let data = new FormData();
        data.append("email", email);
        data.append("password", heslo);

        let config = {
          method: "post",
          maxBodyLength: Infinity,
          url: state.api + "/user/auth/logIn.php",
          data: data,
          withCredentials: true,
        };

        axios
          .request(config)
          .then((response) => {
            setTimeout(() => {
              switch (response.data.status) {
                case "Request fullfiled":
                  state.SnackBarText = "Úspešne prihlásený na účet";
                  this.dispatch("overeniePrihlasenia");
                  if (router.currentRoute.value.path === "/prihlasenie") {
                    router.push("/");
                  }
                  return;
                case "Request failed":
                  switch (response.data.data) {
                    case "Already logged in":
                      state.SnackBarText = "Už ste prihlásený";
                      return;
                    case "Wrong password":
                      state.SnackBarText = "Nesprávne heslo";
                      return;
                    case "Email not found":
                      state.SnackBarText = "Účet s týmto emailom neexistuje";
                      return;
                    case "Trying too often":
                      state.SnackBarText = "Antispam: Príliš často sa pokúšate";
                      return;
                    default:
                      state.SnackBarText = "Neznáma chyba pri prihlásení";
                      return;
                  }
                case "Invalid Request":
                  if (response.data.dovod == "") {
                    state.SnackBarText = "Neboli vyplnené všetky povinné polia";
                  } else if (response.data.dovod != "") {
                    state.SnackBarText = "Vyplňte všetky polia formuláru";
                  } else {
                    state.SnackBarText =
                      "Neznámy dôvod chyby. Prosím skúste neskôr.";
                  }
                  return;
                default:
                  state.SnackBarText =
                    "Neznáma chyba pri prihlásení. Prosím skúste neskôr";
                  return;
              }
            }, 100);
          })
          .catch((error) => {
            console.log(error);
          });
      }, 400);
    },

    async googlePrihlasenie({ state, rootState, dispatch }, payload = {}) {
      const credential = String(payload?.credential || "").trim();
      if (!credential) {
        state.SnackBarText = "Google prihlásenie sa nepodarilo odoslať";
        return;
      }

      const requestPayload = { credential };

      let invite = rootState?.referral?.code || "";
      invite = String(invite).replace(/\/+$/, "");
      if (invite) {
        requestPayload.invite_code = invite;
      }

      try {
        const response = await fetch(state.api + "/user/auth/googleLogIn.php", {
          method: "post",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestPayload),
        });

        const result = await response.json().catch(() => null);

        if (result?.status === "Request fullfiled") {
          const action = result?.data?.action;
          if (action === "register") {
            state.SnackBarText = "Účet bol úspešne vytvorený cez Google";
          } else {
            state.SnackBarText = "Úspešne prihlásený cez Google";
          }

          await dispatch("overeniePrihlasenia");

          if (
            router.currentRoute.value.path === "/prihlasenie" ||
            router.currentRoute.value.path === "/registracia"
          ) {
            router.push("/");
          }

          return;
        }

        if (result?.status === "Request failed") {
          switch (result?.data) {
            case "Already logged in":
              state.SnackBarText = "Už ste prihlásený";
              return;
            case "Google auth not configured":
              state.SnackBarText =
                "Google prihlásenie nie je aktuálne dostupné";
              return;
            case "Invalid Google token":
              state.SnackBarText =
                "Google prihlásenie zlyhalo. Skúste to znova";
              return;
            case "Trying too often":
              state.SnackBarText =
                "Antispam: Príliš často sa pokúšate prihlásiť";
              return;
            default:
              state.SnackBarText = "Neznáma chyba pri Google prihlásení";
              return;
          }
        }

        if (result?.status === "Invalid Request") {
          state.SnackBarText = "Google prihlásenie sa nepodarilo odoslať";
          return;
        }

        state.SnackBarText = "Neznáma chyba pri Google prihlásení";
      } catch (error) {
        console.log(error);
        state.SnackBarText = "Google prihlásenie zlyhalo. Skúste to neskôr";
      }
    },

    registracia(
      { state, rootState },
      { meno, priezvisko, email, heslo, ads, krajina }
    ) {
      const axios = require("axios");
      const FormData = require("form-data");
      let data = new FormData();
      data.append("name", meno);
      data.append("surname", priezvisko);
      data.append("email", email);
      data.append("password", heslo);
      data.append("ads", ads);
      data.append("state", krajina);

      // referral kód (ak existuje)
      let invite = rootState?.referral?.code || "";
      invite = invite.replace(/\/+$/, "");
      if (invite) {
        data.append("invite_code", invite);
      }

      let config = {
        method: "post",
        maxBodyLength: Infinity,
        url: state.api + "/user/auth/register.php",
        data: data,
      };

      axios
        .request(config)
        .then((response) => {
          setTimeout(() => {
            switch (response.data.status) {
              case "Request fullfiled":
                state.SnackBarText = "Účet bol úspešne vytvorený";
                this.dispatch("prihlasenie", { email, heslo });
                router.push("/");
                return;
              case "Request failed":
                switch (response.data.data) {
                  case "Email already registered":
                    state.SnackBarText = "E-mail už je zaregistrovaný";
                    return;
                  case "":
                    state.SnackBarText =
                      "Chyba pripojenia PHP na DB server. Prosím kontaktujte správcu";
                    return;
                  case "Trying too often":
                    state.SnackBarText =
                      "Antispam: Príliš často sa pokúšate prihlásiť";
                    return;
                  default:
                    state.SnackBarText =
                      "Niečo sa pokazilo. Prosím skúste neskôr";
                    return;
                }
              case "Invalid Request":
                if (response.data.dovod == "") {
                  state.SnackBarText = "Neboli vyplnené všetky povinné polia";
                } else if (response.data.dovod != "") {
                  state.SnackBarText = "Polia tela neboli správne vyplnené";
                } else {
                  state.SnackBarText =
                    "Niečo sa pokazilo. Prosím skúste neskôr";
                }
                return;
              default:
                state.SnackBarText = "Niečo sa pokazilo. Prosím skúste neskôr";
                return;
            }
          }, 100);
        })
        .catch((error) => {
          console.log(error);
        });
    },

    fetchCurrentUser({ dispatch }) {
      return dispatch("overeniePrihlasenia");
    },

    async overeniePrihlasenia({ state, commit, dispatch }) {
      const axios = require("axios");

      let config = {
        method: "get",
        maxBodyLength: Infinity,
        url: state.api + "/user/auth/isLoggedIn.php",
        headers: {},
        withCredentials: true,
      };

      try {
        const response = await axios.request(config);
        const status = response?.data?.status || "";
        const data = response?.data?.data || "";
        const isLoggedIn =
          status === "Request fullfiled" && data !== "Not logged in";
        const isAdmin = data === "Is logged in as admin";

        state.authChecked = true;
        state.authLoggedIn = isLoggedIn;
        state.authIsAdmin = isAdmin;

        if (isAdmin) {
          commit("ADD_USER_ADMIN");
        }

        if (status !== "Request fullfiled" || !isLoggedIn) return;

        state.prihlasenyStav++;
        await dispatch("pouzivatelskeInformacie");

        dispatch("pouzivatelAktivnePredplatne");
        dispatch("pouzivatelVlastneneProdukty");
        dispatch("pouzivatelskyKosik");

        if (isAdmin) {
          await dispatch("nacitajRolyPouzivatela");
        }
      } catch (error) {
        state.authChecked = true;
        state.authLoggedIn = false;
        state.authIsAdmin = false;
        console.log(error);
      }
    },

    pouzivatelskeInformacie({ commit, state }) {
      const axios = require("axios");

      let config = {
        method: "get",
        maxBodyLength: Infinity,
        url: state.api + "/user/info/getAdditionalInformation.php",
        headers: {},
        withCredentials: true,
      };

      return axios
        .request(config)
        .then((response) => {
          switch (response.data.status) {
            case "Request succesfull":
              var data = response.data.data;
              data.accesses = JSON.parse(data.accesses);
              commit("SET_USER", data);
              console.log(JSON.stringify(data));
              state.prihlasenyStav++;
              return;
            case "Request failed":
              switch (response.data.data) {
                case "Email not found / Multiple found":
                  state.SnackBarText =
                    "Email neexistuje (účet bol vymazaný) alebo existuje viacero rovnakých emailov.";
                  return;
                case "Not logged in":
                  return;
                case "Trying too often":
                  state.SnackBarText = "Antispam: pokúšate sa príliš často";
                  return;
                default:
                  return;
              }
            default:
              return;
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },

    nacitajRolyPouzivatela({ commit, state, dispatch }) {
      const axios = require("axios");
      const email = state.user?.email;

      if (!email) {
        console.warn(
          "[roles] Preskakujem načítanie rolí, lebo chýba state.user.email."
        );
        return Promise.resolve();
      }

      let config = {
        method: "get",
        maxBodyLength: Infinity,
        url:
          state.api +
          "/user/info/getAllInformationAboutUser.php/?email=" +
          encodeURIComponent(email),
      };

      return axios
        .request(config)
        .then((response) => {
          if (response.data.status === "Request succesfull") {
            var userData = response.data.data;

            let rolesArray = [];

            if (userData?.roles) {
              if (typeof userData.roles === "string") {
                rolesArray = userData.roles
                  .slice(1, -1)
                  .replace(/\\/g, "")
                  .replace(/"/g, "")
                  .replace(/'/g, "")
                  .split(",")
                  .map((item) => item.trim());
              } else if (Array.isArray(userData.roles)) {
                rolesArray = [...userData.roles];
              }
            }

            commit("SET_USER_DATA", rolesArray);

            console.log("this.state.userRoles.roles ", state.userRoles.roles);

            if (state.userRoles?.roles?.includes("product_pass")) {
              dispatch("nacitajNevlastneneZapisy");
            }
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },

    async nacitajNevlastneneZapisy({ commit, state }) {
      const axios = require("axios");

      let config = {
        method: "get",
        maxBodyLength: Infinity,
        url:
          state.api +
          "/user/stats/getZapisNotOwned.php?user_id=" +
          state.user.id,
      };

      try {
        const response = await axios.request(config);

        if (
          response.data.status === "Request succesfull" &&
          Array.isArray(response.data.data)
        ) {
          const newProducts = response.data.data.map((id) => ({
            id: parseInt(id),
            expire: "",
          }));

          commit("ADD_USER_INFO_VLASTNENE_ZAPISY", newProducts);

          console.log(
            "Aktualizované vlastnené produkty:",
            state.user.ownedNotes
          );
        } else {
          console.log("Žiadne nové nevlastnené zápisy neboli nájdené.");
        }
      } catch (error) {
        console.error("Chyba pri načítaní nevlastnených zápisov:", error);
      }
    },

    pouzivatelAktivnePredplatne({ commit, state }) {
      const axios = require("axios");

      let config = {
        method: "get",
        maxBodyLength: Infinity,
        url: state.api + "/subscription/isSubscribed.php/",
        withCredentials: true,
      };

      axios
        .request(config)
        .then((response) => {
          console.log(response.data);
          if (response.data.status == "Request succesfull") {
            if (
              response.data.data == "Is subscribed" ||
              this.state.userRoles?.roles?.includes("product_pass")
            ) {
              commit("ADD_USER_ACTIVE_SUBSCRIPTION");
              console.log("odberá");
            } else if (response.data.data == "Is not subscribed") {
              commit("REMOVE_USER_ACTIVE_SUBSCRIPTION");
              console.log(" neodoberá");
            }

            state.prihlasenyStav++;
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },

    resetovanieHesla({ state }, email) {
      const axios = require("axios");
      const FormData = require("form-data");
      let data = new FormData();
      data.append("email", email);

      let config = {
        method: "post",
        maxBodyLength: Infinity,
        url: state.api + "/user/auth/forgotPasswordSendEmail.php",
        data: data,
      };

      axios
        .request(config)
        .then((response) => {
          switch (response.data.status) {
            case "Request fullfiled":
              console.log(JSON.stringify(response.data.data));
              state.SnackBarText = "Obnovovací email bol zaslaný.";
              router.push("/zabudnute-heslo-uspesne");
              return;
            case "Request failed":
              switch (response.data.data) {
                case "Email not found":
                  state.SnackBarText =
                    "Zadaný email sa nenašiel. Prosím skontrolujte si Vašu emailovú adresu.";
                  return;
                case "Trying too often":
                  state.SnackBarText =
                    "Email bol odoslaný. Další email bude možné odoslať za 1 minútu.";
                  return;
                default:
                  state.SnackBarText =
                    "Niečo sa pokazilo. Ak problém pretrváva prosím kontaktujte nás.";
                  return;
              }
            case "Invalid request":
              state.SnackBarText = "Prosím vyplnte všetky povinné polia";
              return;
            default:
              state.SnackBarText =
                "Niečo sa pokazilo. Ak problém pretrváva prosím kontaktujte nás.";
              return;
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },

    odhlasenie({ state }) {
      const axios = require("axios");

      let config = {
        method: "get",
        maxBodyLength: Infinity,
        url: state.api + "/user/auth/logOut.php",
        headers: {},
        withCredentials: true,
      };

      axios
        .request(config)
        .then((response) => {
          console.log(JSON.stringify(response.data));
          switch (response.data.data) {
            case "":
              state.SnackBarText = "Boli ste úspešne odhlásení";
              state.user = null;
              state.authChecked = true;
              state.authLoggedIn = false;
              state.authIsAdmin = false;
              window.haCanPrint = false;
              router.push("/");
              return;
            case "Not logged in":
              return;
            case "Trying too often":
              state.SnackBarText = "Antispam: Príliš často sa pokúšate";
              return;
            default:
              state.SnackBarText = "Neznáma chyba pri odhlasovaní";
              return;
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },

    pouzivatelskyKosik({ commit, state }) {
      const axios = require("axios");

      let config = {
        method: "get",
        maxBodyLength: Infinity,
        url: state.api + "/cart/load.php",
      };

      axios
        .request(config)
        .then((response) => {
          setTimeout(() => {
            console.log("Dáta z API:", response.data);

            if (response.data.status === "Request fullfiled") {
              const data = response.data.data[0];
              const deleteData = response.data.data[1];

              if (!Array.isArray(data)) {
                console.error(
                  "pouzivatelskyKosik očakáva pole, ale dostal:",
                  data
                );
                return;
              }

              const uniqueItems = data.map((id) => ({
                id:
                  typeof id === "string" && id.startsWith("gift_card")
                    ? id
                    : Number(id),
                count: 1,
                id_delete: id,
                description: "",
              }));

              const uniqueIdsWithCount = Object.entries(deleteData).map(
                ([key, value]) => {
                  const rawId = value[0];
                  const description = value[1];

                  const itemId =
                    typeof rawId === "string" && rawId.startsWith("gift_card")
                      ? rawId
                      : Number(rawId);

                  return {
                    id: itemId,
                    description: description.replaceAll(" - ", " "),
                    count: value.filter((item) => item === rawId).length,
                    id_delete: key,
                  };
                }
              );

              const mergedItems = [
                ...uniqueItems,
                ...uniqueIdsWithCount,
              ].reduce((acc, item) => {
                const existingItem = acc.find(
                  (i) => i.id === item.id && i.description === item.description
                );
                if (existingItem) existingItem.count += item.count;
                else acc.push(item);
                return acc;
              }, []);

              commit("ADD_USER_INFO_KOSIK", mergedItems);
              state.prihlasenyStav++;
              return;
            }

            switch (response.data.data) {
              case "Not logged in":
                console.log(response.data.data);
                return;
              case "Trying too often":
                state.SnackBarText = "Váš košík nebol načítaný správne";
                return;
              default:
                state.SnackBarText = "Váš košík nebol načítaný správne";
                return;
            }
          }, 1000);
        })
        .catch((error) => {
          console.log(error);
        });
    },

    async pridajDoKosika({ state, dispatch }, payload) {
      const axios = require("axios");
      const { id, meta_data } = payload;
      let data = meta_data ? "&cart_real_meta= - " + meta_data : "";

      let config = {
        method: "get",
        maxBodyLength: Infinity,
        url: state.api + "/cart/add.php/?id=" + id + data,
      };

      try {
        const response = await axios.request(config);
        console.log(JSON.stringify(response.data));

        switch (response.data.status) {
          case "Request fullfiled":
            await dispatch("pouzivatelskyKosik");
            state.SnackBarText = "Produkt bol pridaný do košíka";
            break;
          case "Request failed":
            if (response.data.data === "Not logged in") {
              state.SnackBarText =
                "Pre pridanie produktu do košíka musíte byť prihlásený";
            } else if (response.data.data === "Already in cart") {
              state.SnackBarText = "Produkt sa už nachádza vo vašom košíku";
            } else {
              state.SnackBarText = "Nepodarilo sa vložiť produkt do košíka";
            }
            break;
          default:
            state.SnackBarText = "Nepodarilo sa vložiť produkt do košíka";
        }
      } catch (error) {
        console.log(error);
        state.SnackBarText = "Došlo k chybe pri pridávaní produktu do košíka";
      }
    },

    async odstranZKosiku({ state, dispatch }, id) {
      const axios = require("axios");

      let config = {
        method: "get",
        maxBodyLength: Infinity,
        url: state.api + "/cart/remove.php/?id=" + id,
      };

      try {
        const response = await axios.request(config);
        console.log(JSON.stringify(response.data));

        if (response.data.status === "Request fullfiled") {
          await dispatch("pouzivatelskyKosik");
          state.SnackBarText = "Produkt bol odstránený z košíka";
          return true;
        } else {
          state.SnackBarText = "Nepodarilo sa odstrániť produkt";
          return false;
        }
      } catch (error) {
        console.log(error);
        state.SnackBarText = "Chyba pri odstraňovaní produktu";
        return false;
      }
    },

    vytvorPlatbu({ state }) {
      const axios = require("axios");

      let config = {
        method: "get",
        maxBodyLength: Infinity,
        url: state.api + "/cart/paymentCreate.php",
      };

      axios
        .request(config)
        .then((response) => {
          console.log("PaymentCreate response:", response.data);
          const redirectUrl = response.data.data;

          if (redirectUrl.includes("heligonkova")) {
            axios
              .get(redirectUrl)
              .then((r) => {
                window.location.href = "/uspesny-nakup";
                console.log("Heligonkova platba spracovaná na pozadí:", r.data);
              })
              .catch((err) => {
                window.location.href = "/neuspesny-nakup";
                console.error("Chyba pri Heligonkova platbe:", err);
              });
          } else if (redirectUrl.includes("gopay")) {
            window.location.href = redirectUrl;
          } else {
            console.warn("Neznámy payment provider, presmerujem defaultne:");
            window.location.href = redirectUrl;
          }
        })
        .catch((error) => {
          console.error("Error vytvorPlatbu:", error);
          window.location.href = "/neuspesny-nakup";
        });
    },

    pouzivatelVlastneneProdukty({ commit, state }) {
      const axios = require("axios");

      let config = {
        method: "get",
        maxBodyLength: Infinity,
        url: state.api + "/product/getOwned.php",
        withCredentials: true,
      };

      return axios
        .request(config)
        .then((response) => {
          switch (response.data.status) {
            case "Request succesfull":
              if (
                Array.isArray(response.data.data) &&
                response.data.data.length
              ) {
                commit("SET_USER_INFO_VLASTNENE_ZAPISY", response.data.data);
                console.log(JSON.stringify(state.user) + "po pripisaní dát");
              } else {
                commit("SET_USER_INFO_VLASTNENE_ZAPISY", []);
                console.log(JSON.stringify(state.user) + "bez pripísania dát");
              }
              state.prihlasenyStav++;
              return;
            case "Request failed":
              switch (response.data.data) {
                case "Not logged in":
                  console.log(response.data.data);
                  return;
                case "Cart is empty":
                  state.SnackBarText = "Váš košík je prázdny";
                  return;
                default:
                  return;
              }
            default:
              return;
          }
        })
        .catch((error) => {
          console.log(error);
        });
    },
  },
});
